"use client";
import { useEffect, useRef, useState } from "react";
import { c } from "@/lib/vster/theme";

/**
 * 상세 2단 레이아웃(본문 | 정보 사이드바) 사이에 드래그 리사이저.
 *   - 가운데 핸들을 좌우로 끌어 사이드바 너비 조절. 더블클릭 시 기본값 복원.
 *   - 너비는 localStorage 에 저장(뷰어별 편의, try/catch). 좁은 화면(<860px)은 세로 스택 + 핸들 숨김.
 */
const KEY = "program-detail-aside-w";
const DEFAULT_W = 330;
const MIN_W = 280;
const MAX_W = 560;

export default function ResizablePanels({ left, right }: { left: React.ReactNode; right: React.ReactNode }) {
  const [asideW, setAsideW] = useState<number>(DEFAULT_W);
  const [narrow, setNarrow] = useState(false);
  const dragging = useRef(false);

  useEffect(() => {
    try {
      const v = Number(localStorage.getItem(KEY));
      if (Number.isFinite(v) && v >= MIN_W && v <= MAX_W) setAsideW(v);
    } catch { /* 저장소 접근 불가(프라이빗 등) 무시 */ }
    const mq = window.matchMedia("(max-width: 860px)");
    const on = () => setNarrow(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  useEffect(() => {
    if (narrow) return;
    const move = (e: PointerEvent) => {
      if (!dragging.current) return;
      // 핸들을 왼쪽으로 끌수록 사이드바가 넓어짐 → 오른쪽 가장자리 - 커서X.
      const right = document.getElementById("detail-aside-col");
      const edge = right ? right.getBoundingClientRect().right : window.innerWidth;
      const w = Math.min(MAX_W, Math.max(MIN_W, edge - e.clientX));
      setAsideW(w);
    };
    const up = () => {
      if (!dragging.current) return;
      dragging.current = false;
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      try { localStorage.setItem(KEY, String(Math.round(asideW))); } catch { /* 무시 */ }
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
  }, [narrow, asideW]);

  const startDrag = (e: React.PointerEvent) => {
    if (narrow) return;
    e.preventDefault();
    dragging.current = true;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
  };

  if (narrow) {
    // 좁은 화면: 세로 스택(리사이저 없음).
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 18, alignItems: "stretch" }}>
        <div style={{ minWidth: 0 }}>{left}</div>
        <div id="detail-aside-col" style={{ minWidth: 0 }}>{right}</div>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: `minmax(0,1fr) 14px ${asideW}px`, alignItems: "start" }}>
      <div style={{ minWidth: 0 }}>{left}</div>
      {/* 리사이저 핸들 */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="본문과 정보 영역 너비 조절"
        onPointerDown={startDrag}
        onDoubleClick={() => { setAsideW(DEFAULT_W); try { localStorage.setItem(KEY, String(DEFAULT_W)); } catch { /* 무시 */ } }}
        title="드래그해서 너비 조절 (더블클릭: 기본값)"
        style={{ alignSelf: "stretch", display: "flex", alignItems: "center", justifyContent: "center", cursor: "col-resize", touchAction: "none" }}
      >
        <div style={{ width: 4, height: 44, borderRadius: 4, background: c.line, transition: "background 120ms" }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = c.action; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = c.line; }}
        />
      </div>
      <div id="detail-aside-col" style={{ minWidth: 0 }}>{right}</div>
    </div>
  );
}
